import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as svc from './pagos-service';
import { pagosKeys } from './keys';
import { clientesKeys } from '../../clientes/api/keys';
import { useDebouncedValue } from '../../../lib/useDebouncedValue';
import { PAGO_MODALIDAD, type PagoModalidad } from '@aid/shared';
import type {
  CreatePagoDto,
  PagoPrepagoPreviewRequest,
  QueryPagosDto,
  AnularPagoDto,
} from './pagos-service';

export function usePagoCoordenadas(clienteId: number) {
  return useQuery({
    queryKey: pagosKeys.coordenadas(clienteId),
    queryFn: () => svc.getPagoCoordenadas(clienteId),
    enabled: clienteId > 0,
    staleTime: 30_000,
  });
}

interface UsePagosListOptions {
  /** Caller gate: a disabled list query fires no request at all. */
  enabled?: boolean;
}

export function usePagosList(params: QueryPagosDto, options?: UsePagosListOptions) {
  // SAFETY: Query DTOs are JSON-compatible records; the key helper only needs this structural view.
  return useQuery({
    queryKey: pagosKeys.list(params as unknown as Record<string, unknown>),
    queryFn: () => svc.getPagos(params),
    staleTime: 30_000,
    enabled: options?.enabled ?? true,
  });
}

interface UsePagosStatsOptions {
  enabled?: boolean;
}

export function usePagosStats(
  params: Omit<QueryPagosDto, 'offset' | 'limit'>,
  options?: UsePagosStatsOptions,
) {
  // SAFETY: Query DTOs are JSON-compatible records; the key helper only needs this structural view.
  return useQuery({
    queryKey: pagosKeys.stats(params as unknown as Record<string, unknown>),
    queryFn: () => svc.getPagosStats(params),
    staleTime: 30_000,
    enabled: options?.enabled ?? true,
  });
}

export function usePago(id: number) {
  return useQuery({
    queryKey: pagosKeys.detail(id),
    queryFn: () => svc.getPago(id),
    enabled: id > 0,
    staleTime: 30_000,
  });
}

interface UsePagosUsersOptions {
  enabled?: boolean;
}

/**
 * Option list for the "filter by user" control: distinct users associated
 * with payments. Loaded only where the control is visible; the toolbar runs
 * client-side search, so no debounced server queries are needed.
 */
export function usePagosUsers(options?: UsePagosUsersOptions) {
  return useQuery({
    queryKey: pagosKeys.users(),
    queryFn: () => svc.getPagosUsers(),
    staleTime: 5 * 60_000,
    enabled: options?.enabled ?? true,
  });
}

export function useBoletaPago(id: number) {
  return useQuery({
    queryKey: pagosKeys.boleta(id),
    queryFn: () => svc.descargarBoletaPago(id),
    enabled: id > 0,
    staleTime: 30_000,
  });
}

interface UsePagoFifoPreviewOptions {
  clienteId: number;
  montoPago: number;
  modalidad: PagoModalidad;
  /**
   * Targeted selection: when present the forecast covers ONLY these invoices
   * (oldest first) instead of every open invoice.
   */
  idFacturasPago?: readonly number[];
  /** Optional caller gate: the prepaid screen probes a fixed amount instead. */
  enabled?: boolean;
}

/**
 * Debounced, read-only FIFO preview. ADELANTADO is deliberately disabled so
 * it cannot be silently treated as a NORMAL payment.
 */
export function usePagoFifoPreview({
  clienteId,
  montoPago,
  modalidad,
  idFacturasPago,
  enabled: requested = true,
}: UsePagoFifoPreviewOptions) {
  const debouncedAmount = useDebouncedValue(montoPago);
  const validClient = Number.isInteger(clienteId) && clienteId > 0;
  const currentAmountValid = Number.isFinite(montoPago) && montoPago > 0;
  const debouncedAmountValid = Number.isFinite(debouncedAmount) && debouncedAmount > 0;
  const enabled =
    requested &&
    modalidad === PAGO_MODALIDAD.NORMAL &&
    validClient &&
    currentAmountValid &&
    debouncedAmountValid;
  const requestAmount = debouncedAmount > 0 ? debouncedAmount.toFixed(2) : '0.00';
  // The database decides the distribution order (oldest first), so the caller's
  // ticking order must not churn the cache: dedupe and sort the request.
  const seleccion =
    idFacturasPago === undefined ? undefined : [...new Set(idFacturasPago)].sort((a, b) => a - b);

  const query = useQuery({
    queryKey: pagosKeys.fifoPreview({
      idCliente: clienteId,
      montoPago: requestAmount,
      modalidad,
      idFacturasPago: seleccion,
    }),
    queryFn: () =>
      svc.previewPagoFifo({
        idCliente: clienteId,
        montoPago: requestAmount,
        modalidad: PAGO_MODALIDAD.NORMAL,
        ...(seleccion === undefined ? {} : { idFacturasPago: seleccion }),
      }),
    enabled,
    staleTime: 0,
  });

  return {
    ...query,
    isDebouncing:
      requested &&
      modalidad === PAGO_MODALIDAD.NORMAL &&
      validClient &&
      Number.isFinite(montoPago) &&
      montoPago > 0 &&
      debouncedAmount !== montoPago,
  };
}

interface UsePagoPrepagoPreviewOptions {
  clienteId: number;
  mesesAdelantado: number;
  fechaBase?: string;
  enabled: boolean;
}

export function usePagoPrepagoPreview({
  clienteId,
  mesesAdelantado,
  fechaBase,
  enabled: requested,
}: UsePagoPrepagoPreviewOptions) {
  const validClient = Number.isInteger(clienteId) && clienteId > 0;
  const validMonths =
    Number.isInteger(mesesAdelantado) && mesesAdelantado >= 1 && mesesAdelantado <= 12;
  const enabled = requested && validClient && validMonths;
  const request: PagoPrepagoPreviewRequest = {
    idCliente: clienteId,
    mesesAdelantado,
    ...(fechaBase === undefined ? {} : { fechaBase }),
  };

  // SAFETY: Query DTOs are JSON-compatible records; the key helper only needs this structural view.
  return useQuery({
    queryKey: pagosKeys.prepaidPreview(request as unknown as Record<string, unknown>),
    queryFn: () => svc.previewPagoPrepago(request),
    enabled,
    staleTime: 0,
  });
}

export function useCreatePago() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreatePagoDto) => svc.createPago(dto),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: pagosKeys.lists() });
      qc.invalidateQueries({ queryKey: clientesKeys.serieMensualBase(variables.idCliente) });
    },
  });
}

/** OCR de boleta: sube la foto y devuelve los candidatos para confirmar. */
export function useBoletaOcr() {
  return useMutation({
    mutationFn: (file: File) => svc.uploadBoletaOcr(file),
  });
}

export function useAnularPago() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: number; dto: AnularPagoDto }) => svc.anularPago(id, dto),
    onSuccess: (data, variables) => {
      qc.invalidateQueries({ queryKey: pagosKeys.detail(variables.id) });
      qc.invalidateQueries({ queryKey: pagosKeys.lists() });
      qc.invalidateQueries({ queryKey: clientesKeys.detail(data.idCliente) });
      qc.invalidateQueries({ queryKey: clientesKeys.cobros(data.idCliente) });
      qc.invalidateQueries({ queryKey: clientesKeys.serieMensualBase(data.idCliente) });
    },
  });
}
